import React, { useEffect, useRef } from 'react';
import { Box, MenuItem, Select } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment from 'moment';
import { useFormContext } from 'react-hook-form';
import { FieldRenderProps } from '../PopulateCardField';
import {
  ProjectStackField,
  ProjectStackDocument,
  SUPPORTED_DATE_FORMATS,
  FORMAT_OVERRIDE_PREFIX,
  reformatDateValue,
} from '~/src/entities/project';
import FieldHeader from '../FieldHeader';
import { getLocalDateFormat } from '~/src/utils/date';

export type DatePickerFieldProps = {
  field: FieldRenderProps;
  projectField: ProjectStackField;
  projectStackDocuments: ProjectStackDocument[];
  onFirstChange?: () => void;
  hasActiveMapping?: boolean;
  orgDateFormat?: string;
  isDateFieldPickerEnabled?: boolean;
};

function parseISOStringToTimezoneConvertedMoment(
  dateString: string,
): moment.Moment | null {
  const momentDate = moment.utc(dateString, moment.ISO_8601, true);
  if (momentDate.isValid() && dateString.includes('T')) {
    return momentDate.local();
  }
  return null;
}

function injectTimezoneWhileKeepingSameDateTime(
  date: moment.Moment,
): moment.Moment {
  return date.utcOffset(moment().utcOffset(), true);
}

export const DatePickerField = React.memo(
  ({
    field,
    projectField,
    projectStackDocuments,
    onFirstChange,
    hasActiveMapping,
    orgDateFormat,
    isDateFieldPickerEnabled = false,
  }: DatePickerFieldProps) => {
    const { value, onChange, countTouched, ref, ...rest } = field;
    const formContext = useFormContext();
    const overrideKey = `${FORMAT_OVERRIDE_PREFIX}${projectField.fid}`;
    const overrideFormat = formContext?.watch(overrideKey) as
      | string
      | undefined;

    const shouldShowFormatPicker =
      isDateFieldPickerEnabled && !!projectField.date_format;
    const format =
      (shouldShowFormatPicker && overrideFormat) ||
      projectField.date_format ||
      orgDateFormat ||
      getLocalDateFormat();

    // Ensure the active format is always selectable
    const formatOptions = SUPPORTED_DATE_FORMATS.includes(format)
      ? SUPPORTED_DATE_FORMATS
      : [format, ...SUPPORTED_DATE_FORMATS];

    const skipNextNormalizeRef = useRef(false);
    const valueRef = useRef(value);
    valueRef.current = value;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const handleFormatChange = (newFormat: string) => {
      formContext?.setValue(overrideKey, newFormat, { shouldDirty: true });
      if (value) {
        const formatted = reformatDateValue(value, newFormat, format);
        if (formatted) {
          skipNextNormalizeRef.current = true;
          onChange(formatted);
        }
      }
    };

    const parseDateStringToMoment = (dateString: string) => {
      if (!dateString) return null;

      const momentDate = parseISOStringToTimezoneConvertedMoment(dateString);
      if (momentDate) return momentDate;

      const momentWithFormat = moment.utc(dateString, format, true);
      if (momentWithFormat.isValid()) {
        return injectTimezoneWhileKeepingSameDateTime(momentWithFormat);
      }

      for (const commonFormat of SUPPORTED_DATE_FORMATS) {
        const parsedDate = moment.utc(dateString, commonFormat, true);
        if (parsedDate.isValid()) {
          return injectTimezoneWhileKeepingSameDateTime(parsedDate);
        }
      }

      const parsedDate = moment.utc(dateString);
      if (parsedDate.isValid()) {
        return injectTimezoneWhileKeepingSameDateTime(parsedDate);
      }

      return null;
    };

    // Normalize value when format changes externally. Reads value/onChange
    // through refs so we only fire on format changes, not keystrokes.
    useEffect(() => {
      if (skipNextNormalizeRef.current) {
        skipNextNormalizeRef.current = false;
        return;
      }
      const current = valueRef.current;
      const formatted = reformatDateValue(current, format);
      if (formatted && formatted !== current) onChangeRef.current(formatted);
    }, [format]);

    const handleChange = (momentObj: moment.Moment | null) => {
      onFirstChange?.();
      if (momentObj && momentObj.isValid()) {
        onChange(momentObj.format(format));
      } else {
        onChange('');
      }
    };

    return (
      <Box>
        <Box>
          <FieldHeader
            projectField={projectField}
            projectStackDocuments={projectStackDocuments}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    placeholder: format,
                    sx: hasActiveMapping
                      ? {
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: (theme) => theme.palette.blue[50],
                          },
                        }
                      : undefined,
                  },
                  field: { shouldRespectLeadingZeros: true },
                }}
                inputRef={ref}
                format={format}
                value={parseDateStringToMoment(value)}
                onChange={(evt) => {
                  handleChange(evt);
                  if (countTouched) {
                    countTouched(field.name);
                  }
                }}
                {...rest}
              />
            </LocalizationProvider>
          </Box>
          {shouldShowFormatPicker && (
            <Select
              size="small"
              value={format}
              onChange={(e) => handleFormatChange(e.target.value)}
              aria-label="date format"
              sx={{ minWidth: 130 }}
            >
              {formatOptions.map((fmt) => (
                <MenuItem key={fmt} value={fmt}>
                  {fmt}
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>
      </Box>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.field.value === nextProps.field.value &&
      prevProps.projectField === nextProps.projectField &&
      prevProps.projectStackDocuments === nextProps.projectStackDocuments &&
      prevProps.onFirstChange === nextProps.onFirstChange &&
      prevProps.hasActiveMapping === nextProps.hasActiveMapping &&
      prevProps.orgDateFormat === nextProps.orgDateFormat &&
      prevProps.isDateFieldPickerEnabled === nextProps.isDateFieldPickerEnabled
    );
  },
);
